import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { IDENTITY_TAG_SUGGESTIONS, LANGUAGE_SUGGESTIONS, COLLAB_FIELDS, AGE_RANGES } from "@/lib/options";
import { COUNTRIES, getStates, getCities, hasCascade } from "@/lib/locations";
import { Search, MapPin, X, Filter, Heart, ChevronDown, ChevronLeft, ChevronRight, Users } from "lucide-react";

function Checkbox({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group py-1">
      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all
        ${checked ? "bg-wine border-black" : "bg-linen border-black group-hover:bg-mustard"}`}>
        {checked && <span className="w-1.5 h-1.5 bg-white rounded-sm"/>}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e)=>onChange(e.target.checked)} data-testid={testid}/>
      <span className="text-sm text-ink">{label}</span>
    </label>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b-2 border-black/15 py-5">
      <button onClick={()=>setOpen(!open)} className="w-full flex items-center justify-between text-ink">
        <span className="font-serif text-lg">{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open?"rotate-180":""}`} strokeWidth={1.5}/>
      </button>
      {open && <div className="mt-3 space-y-0.5">{children}</div>}
    </div>
  );
}

export default function Directory() {
  const { user } = useAuth();
  const [data, setData] = useState({ results: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [city, setCity] = useState("");
  const [identityTags, setIdentityTags] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [collab, setCollab] = useState([]);
  const [ageRange, setAgeRange] = useState("");
  const [mobileFilters, setMobileFilters] = useState(false);

  const pending = user?.role === "vendor" && user?.vendor_status !== "approved";

  const PAGE_SIZE = 24;
  const [page, setPage] = useState(1);

  const buildParams = useCallback(() => {
    const params = { limit: PAGE_SIZE };
    if (q) params.q = q;
    const stateForSearch = stateRegion === "__other__" ? "" : stateRegion;
    const locTerm = city || stateForSearch || country;
    if (locTerm) params.location = locTerm;
    if (identityTags.length) params.identity_tags = identityTags.join(",");
    if (languages.length) params.languages = languages.join(",");
    if (collab.length) params.collab = collab.join(",");
    if (ageRange) params.age_range = ageRange;
    return params;
  }, [q, country, stateRegion, city, identityTags, languages, collab, ageRange]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [q, country, stateRegion, city, identityTags, languages, collab, ageRange]);

  // Load whenever page or filters change
  const load = useCallback(async () => {
    if (pending) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { ...buildParams(), page };
      const { data: d } = await api.get("/directory/couples", { params });
      setData({ results: d.results || [], total: d.total || 0 });
      // Scroll to top of grid on page change (skip on initial load)
      if (page > 1) window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setData({ results: [], total: 0 });
    } finally { setLoading(false); }
  }, [buildParams, pending, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const clearAll = () => {
    setQ(""); setCountry(""); setStateRegion(""); setCity(""); setIdentityTags([]); setLanguages([]); setCollab([]); setAgeRange("");
  };

  if (pending) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="qld-overline mb-4">vendor application received</p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink mb-6">Hang tight — we're reviewing.</h1>
          <p className="text-muted text-lg mb-8">
            Vendor accounts are manually approved within 24 hours. We'll email you the moment you're in.
          </p>
          <Link to="/dashboard/vendor" className="qld-btn-primary">Go to my dashboard</Link>
        </div>
      </Layout>
    );
  }

  const FilterPanel = (
    <aside className="bg-lilac border-2 border-black rounded-3xl p-6 sticky top-24">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-2xl text-ink">Filters</h3>
        <button onClick={clearAll} className="text-xs uppercase tracking-[0.18em] font-semibold text-wine hover:underline transition" data-testid="filters-clear">
          Clear all
        </button>
      </div>
      <Section title="Location">
        <div className="space-y-2.5">
          <select data-testid="filter-country" value={country} onChange={(e)=>{ setCountry(e.target.value); setStateRegion(""); setCity(""); }}
                  className="qld-input">
            <option value="">All countries</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {country && hasCascade(country) && (
            <>
              <select data-testid="filter-state" value={stateRegion} onChange={(e)=>{ setStateRegion(e.target.value); setCity(""); }}
                      className="qld-input">
                <option value="">All states / regions</option>
                {getStates(country).map((s) => <option key={s} value={s}>{s}</option>)}
                <option value="__other__">Other (type a town or region)</option>
              </select>
              {stateRegion === "__other__" ? (
                <input data-testid="filter-state-other" value={city} onChange={(e)=>setCity(e.target.value)}
                       placeholder="Type a city, town or region…" className="qld-input" autoFocus/>
              ) : stateRegion ? (
                <select data-testid="filter-city" value={city} onChange={(e)=>setCity(e.target.value)}
                        className="qld-input">
                  <option value="">All cities</option>
                  {getCities(country, stateRegion).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : null}
              <p className="text-xs text-black/60 italic">
                {stateRegion === "__other__"
                  ? `We'll search anywhere in ${country} that matches your text.`
                  : stateRegion
                    ? `Leave city blank to see every listing in ${stateRegion}.`
                    : `Leave state blank to see every listing in ${country}.`}
              </p>
            </>
          )}
          {country && !hasCascade(country) && (
            <p className="text-xs text-black/60 italic">Showing every listing in {country}.</p>
          )}
        </div>
      </Section>
      <Section title="Open to">
        {COLLAB_FIELDS.map((c) => (
          <Checkbox key={c.key} label={c.label}
            checked={collab.includes(c.key.replace("open_to_","").replace("_","s").replace("dayss","_days"))}
            onChange={()=>{
              const v = c.key === "open_to_styled_shoots" ? "styled_shoots"
                      : c.key === "open_to_editorial" ? "editorial" : "content_days";
              toggle(collab, setCollab, v);
            }}
            testid={`filter-collab-${c.key}`}/>
        ))}
      </Section>
      <Section title="Identity tags" defaultOpen={false}>
        <p className="text-xs text-muted mb-2 italic">Completely optional — lovers self-select these.</p>
        <div className="max-h-56 overflow-y-auto pr-1 -mr-1">
          {IDENTITY_TAG_SUGGESTIONS.map((i) => (
            <Checkbox key={i} label={i} checked={identityTags.includes(i)}
              onChange={()=>toggle(identityTags, setIdentityTags, i)} testid={`filter-identity-${i.toLowerCase().replace(/\s|\+|\//g,"-")}`}/>
          ))}
        </div>
      </Section>
      <Section title="Languages" defaultOpen={false}>
        <div className="max-h-48 overflow-y-auto pr-1 -mr-1">
          {LANGUAGE_SUGGESTIONS.map((l) => (
            <Checkbox key={l} label={l} checked={languages.includes(l)}
              onChange={()=>toggle(languages, setLanguages, l)} testid={`filter-lang-${l.toLowerCase().replace(/[^\w]+/g,"-")}`}/>
          ))}
        </div>
      </Section>
      <Section title="Age range" defaultOpen={false}>
        <select className="qld-input mt-1" value={ageRange} onChange={(e)=>setAgeRange(e.target.value)} data-testid="filter-age-range">
          <option value="">Any age</option>
          {AGE_RANGES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </Section>
    </aside>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="qld-overline mb-3">the directory</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-tight">
              Meet the lovers.
            </h1>
            <p className="text-muted mt-3 max-w-xl">
              Find real lovers, with diverse identities and intersections who want to help make the wedding world one that shows love that looks just like theirs. Filter by location, availability, language and self described identities.
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="w-4 h-4 text-muted absolute left-4 top-1/2 -translate-y-1/2" strokeWidth={1.5}/>
            <input data-testid="directory-search-input" value={q} onChange={(e)=>setQ(e.target.value)}
              placeholder="Search by name, city, story…" className="qld-input !pl-10"/>
          </div>
        </div>

        <button onClick={()=>setMobileFilters(true)} className="lg:hidden qld-btn-ghost mb-4 inline-flex" data-testid="mobile-filters-open">
          <Filter className="w-4 h-4" strokeWidth={1.5}/> Filters
        </button>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="hidden lg:block lg:col-span-3">{FilterPanel}</div>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-muted" data-testid="directory-count">
                {loading
                  ? "Loading…"
                  : data.total === 0
                    ? "0 lovers"
                    : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, data.total)} of ${data.total} lovers`}
              </p>
            </div>
            {loading ? (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="qld-card animate-pulse">
                    <div className="aspect-[4/5] bg-black/10"/>
                    <div className="p-5 space-y-2">
                      <div className="h-4 bg-black/10 rounded w-2/3"/>
                      <div className="h-3 bg-black/10 rounded w-1/2"/>
                    </div>
                  </div>
                ))}
              </div>
            ) : data.results.length === 0 ? (
              <div className="qld-card p-12 text-center">
                <Users className="w-8 h-8 mx-auto text-wine mb-3" strokeWidth={1.5}/>
                <h3 className="font-serif text-2xl text-ink mb-2">No lovers match yet.</h3>
                <p className="text-muted">Try loosening your filters — or check back as the platform grows.</p>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="directory-grid">
                  {data.results.map((p) => <CoupleCard key={p.user_id} p={p}/>)}
                </div>
                {totalPages > 1 && (
                  <Pagination page={page} totalPages={totalPages} onChange={setPage}/>
                )}
              </>
            )}
          </div>
        </div>

        {mobileFilters && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50 flex items-end" onClick={()=>setMobileFilters(false)}>
            <div className="bg-linen w-full rounded-t-3xl border-t-2 border-x-2 border-black p-6 max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl">Filters</h3>
                <button onClick={()=>setMobileFilters(false)}><X className="w-5 h-5"/></button>
              </div>
              {FilterPanel}
              <button onClick={()=>setMobileFilters(false)} className="qld-btn-primary w-full mt-6">Show {data.total} results</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function CoupleCard({ p }) {
  const photo = p.photos?.[0];
  const collabs = [];
  if (p.open_to_styled_shoots) collabs.push("Styled shoots");
  if (p.open_to_editorial) collabs.push("Editorial");
  if (p.open_to_content_days) collabs.push("Content days");
  return (
    <Link to={`/couples/${p.user_id}`} data-testid={`couple-card-${p.user_id}`} className="qld-card group block overflow-hidden">
      <div className="relative aspect-[3/4] bg-powder overflow-hidden border-b-2 border-black">
        {photo ? (
          <img src={imgUrl(photo)} alt={p.display_name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-wine">
            <Heart className="w-12 h-12" strokeWidth={1}/>
          </div>
        )}
        {p.pronouns && (
          <span className="absolute top-3 left-3 qld-chip bg-linen">
            {p.pronouns}{p.partner_pronouns ? ` · ${p.partner_pronouns}` : ""}
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-ink truncate">{p.display_name}</h3>
        {(p.location_city || p.location_state || p.location_country) && (
          <p className="text-sm text-muted mt-1 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/>
            {[p.location_city, p.location_state, p.location_country].filter(Boolean).join(", ")}
          </p>
        )}
        {collabs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {collabs.map((c) => <span key={c} className="qld-chip qld-chip-sage !text-xs !py-0.5 !px-2.5">{c}</span>)}
          </div>
        )}
      </div>
    </Link>
  );
}


/* ── Numbered pagination ────────────────────────────────────────────────
 * Shows: ‹ 1 … 4 5 [6] 7 8 … 26 ›
 * Always shows first + last; window of 2 either side of current.
 */
function pageRange(current, total) {
  const out = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  return [...out].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
}

function Pagination({ page, totalPages, onChange }) {
  const pages = pageRange(page, totalPages);
  const btn = "inline-flex items-center justify-center min-w-[40px] h-10 px-3 border-2 border-black rounded-full text-sm font-semibold transition";
  return (
    <nav
      aria-label="Directory pagination"
      data-testid="directory-pagination"
      className="mt-10 flex flex-wrap items-center justify-center gap-2"
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        data-testid="pagination-prev"
        className={`${btn} bg-linen hover:bg-mustard disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={2}/>
        <span className="hidden sm:inline ml-1">Prev</span>
      </button>

      {pages.map((n, i) => {
        const prev = pages[i - 1];
        const gap = prev && n - prev > 1;
        return (
          <React.Fragment key={n}>
            {gap && <span className="px-1 text-black/50 select-none">…</span>}
            <button
              type="button"
              onClick={() => onChange(n)}
              data-testid={`pagination-page-${n}`}
              aria-current={n === page ? "page" : undefined}
              className={`${btn} ${n === page ? "bg-wine text-white" : "bg-linen hover:bg-mustard"}`}
            >
              {n}
            </button>
          </React.Fragment>
        );
      })}

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        data-testid="pagination-next"
        className={`${btn} bg-linen hover:bg-mustard disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <span className="hidden sm:inline mr-1">Next</span>
        <ChevronRight className="w-4 h-4" strokeWidth={2}/>
      </button>
    </nav>
  );
}
